import React, { useState } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Divider } from "primereact/divider";
import { NotTakenExamsReportResponse } from "../../../models/dtos";
import { facultiesApi } from "../../../services/nomenclatures/facultiesApi";
import { studentsApi } from "../../../services/students/studentsApi";
import CustomDropdown from "../../../components/forms/CustomDropdown";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";

const NotTakenExamsReport: React.FC = () => {
  const [facultyId, setFacultyId] = useState<number>();
  const [year, setYear] = useState<number>(1);

  const { data: faculties } = facultiesApi.useGetFacultiesQuery();
  const { data: report, isFetching } = studentsApi.useGetNotTakenExamsReportQuery(
    { facultyId: facultyId!, year },
    { skip: !facultyId },
  );

  const years = [1, 2, 3, 4, 5, 6];

  const exportToExcel = () => {
    if (!report?.data) return;

    const exportData = report.data.map(item => ({
      'Факултетен номер': item.facultyNumber,
      'Име': item.fullName,
      'Факултет': item.facultyName,
      'ОКС': item.degreeName,
      'Специалност': item.specialityName,
      'Статус': item.status,
      'Семестър': item.semester,
      'Курс': item.year,
      'Брой неположени изпити': item.notTakenExamsCount,
      'Неположени изпити': item.notTakenExamNames
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    worksheet['!autofilter'] = { ref: worksheet['!ref'] || 'A1' };
    
    worksheet['!cols'] = [
      { wch: 15 }, // Факултетен номер
      { wch: 30 }, // Име
      { wch: 30 }, // Факултет
      { wch: 10 }, // ОКС
      { wch: 30 }, // Специалност
      { wch: 15 }, // Статус
      { wch: 10 }, // Семестър
      { wch: 10 }, // Курс
      { wch: 20 }  // Брой неположени изпити
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Неположени изпити');
    XLSX.writeFile(workbook, 'справка-неположени-изпити.xlsx');
  };

  return (
    <div className="flex justify-content-center flex-wrap">
      <Card className="w-full mt-4">
        <div className="flex justify-content-between align-items-center">
          <h4>Справка за неположени изпити</h4>
          <Button
            icon="pi pi-file-excel"
            label="Експорт"
            className="p-button-success"
            onClick={exportToExcel}
            disabled={!report?.data || isFetching}
          />
        </div>
        <Divider />
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label htmlFor="facultyId">Факултет</label>
            <Dropdown
              id="facultyId"
              options={faculties?.data ?? []}
              optionLabel="name"
              optionValue="facultyId"
              placeholder="Изберете факултет"
              value={facultyId}
              onChange={(e: DropdownChangeEvent) => setFacultyId(e.value)}
              required
              className="w-full"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="year">Година</label>
            <Dropdown
              id="year"
              options={years}
              optionLabel="name"
              optionValue="id"
              placeholder="Изберете година"
              value={year}
              onChange={(e: DropdownChangeEvent) => setYear(e.value)}
              required
              className="w-full"
            />
          </div>
          <div className="field col-12">
            <DataTable
              value={report?.data}
              loading={isFetching}
              emptyMessage="Няма намерени резултати"
              stripedRows
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              sortField="facultyNumber"
              sortOrder={1}
            >
              <Column
                field="facultyNumber"
                header="Факултетен номер"
                sortable
                body={(rowData) => (
                  <Link to={`/students/${rowData.studentId}`} className="text-blue-500">
                    {rowData.facultyNumber}
                  </Link>
                )}
              />
              <Column field="fullName" header="Име" sortable />
              <Column field="facultyName" header="Факултет" sortable />
              <Column field="degreeName" header="ОКС" sortable />
              <Column field="specialityName" header="Специалност" sortable />
              <Column field="status" header="Статус" sortable />
              <Column field="semester" header="Семестър" sortable />
              <Column field="year" header="Курс" sortable />
              <Column field="notTakenExamsCount" header="Брой неположени изпити" sortable />
              <Column field="notTakenExamNames" header="Неположени изпити" sortable />
            </DataTable>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default NotTakenExamsReport;
